import {useCallback, useEffect, useRef, useState} from "react";
import {mobileImage} from "../mobileAssets";

const characterNameMarginLeft = {
    "P2_07.png": ".3rem",
    "P2_14.png": ".2rem",
    "P2_16.png": ".36rem"
};

export default function CharacterDetailScreen({character, characterIndex = 0, onChangeCharacter, onBack, videoSrc, onVoiceStart, onVoiceEnd}) {
    const pointerRef = useRef(null);
    const animationTimerRef = useRef(null);
    const audioRef = useRef(null);
    const wasMusicPlayingBeforeVoiceRef = useRef(false);
    const [previousCharacter, setPreviousCharacter] = useState(character);
    const [transition, setTransition] = useState(null);
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [mediaError, setMediaError] = useState("");

    useEffect(() => {
        if (!transition) setPreviousCharacter(character);
    }, [character, transition]);

    const stopAudio = useCallback(function (restoreMusic = true) {
        const audio = audioRef.current;
        audioRef.current = null;
        if (audio) {
            audio.onended = null;
            audio.onerror = null;
            audio.pause();
            audio.currentTime = 0;
        }
        if (restoreMusic) {
            if (wasMusicPlayingBeforeVoiceRef.current && typeof onVoiceEnd === "function") {
                onVoiceEnd(true);
            }
            wasMusicPlayingBeforeVoiceRef.current = false;
        }
        setIsAudioPlaying(false);
    }, [onVoiceEnd]);

    useEffect(() => () => {
        if (animationTimerRef.current) window.clearTimeout(animationTimerRef.current);
        stopAudio();
    }, [stopAudio]);

    useEffect(() => {
        setIsVideoOpen(false);
        setIsAudioPlaying(false);
        setMediaError("");
        stopAudio();
    }, [character, stopAudio]);

    const requestCharacterChange = function (offset) {
        if (transition) return;
        setPreviousCharacter(character);
        setTransition({direction: offset > 0 ? "next" : "prev"});
        onChangeCharacter(offset);
        animationTimerRef.current = window.setTimeout(function () {
            setTransition(null);
            animationTimerRef.current = null;
        }, 520);
    };

    const handlePointerDown = function (event) {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        if (event.target.closest && event.target.closest("button")) return;
        pointerRef.current = {id: event.pointerId, x: event.clientX, y: event.clientY};
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handlePointerUp = function (event) {
        const pointer = pointerRef.current;
        if (!pointer || pointer.id !== event.pointerId) return;
        pointerRef.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        const deltaX = event.clientX - pointer.x;
        const deltaY = event.clientY - pointer.y;
        if (Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
            requestCharacterChange(deltaX < 0 ? 1 : -1);
        }
    };

    const toggleAudio = function () {
        const audioUrl = character.audio || character.audioUrl;
        if (!audioUrl) {
            setMediaError("角色语音暂未配置");
            return;
        }
        stopAudio(false);
        wasMusicPlayingBeforeVoiceRef.current = typeof onVoiceStart === "function" && onVoiceStart() === true;
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.onended = function () { stopAudio(); };
        audio.onerror = function () {
            stopAudio();
            setIsAudioPlaying(false);
            setMediaError("角色语音加载失败");
        };
        audio.play().then(function () {
            setMediaError("");
            setIsAudioPlaying(true);
        }).catch(function () {
            stopAudio();
            setMediaError("角色语音加载失败");
        });
    };

    const renderLayer = function (itemCharacter, className, showControls) {
        return (
            <div className={`character-slide-layer ${className}`}>
                <img src={mobileImage(itemCharacter.hero)} className="picture" alt="角色立绘"/>
                <div className="info">
                    <div className="t">
                        <img src={mobileImage(itemCharacter.name)} className="name" style={characterNameMarginLeft[itemCharacter.name] ? {marginLeft: characterNameMarginLeft[itemCharacter.name]} : undefined} alt="角色名称"/>
                        {showControls ? <>
                            <button className="character-video-play" type="button" aria-label="播放角色视频" onClick={() => { setMediaError(""); setIsVideoOpen(true); }}><img src={mobileImage("play.png")} className="play" alt=""/></button>
                            <button type="button" aria-label={isAudioPlaying ? "暂停角色语音" : "播放角色语音"} onClick={toggleAudio}><img src={mobileImage("yy.png")} className="speek" alt=""/></button>
                        </> : null}
                    </div>
                    <p className="desc">{itemCharacter.description}</p>
                    {showControls && mediaError ? <p className="character-media-error" role="status">{mediaError}</p> : null}
                </div>
            </div>
        );
    };

    const videoUrl = videoSrc || character.video || character.videoUrl;

    return (
        <div
            className={`page_the${transition ? ` is-transitioning ${transition.direction}` : ""}`}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => { pointerRef.current = null; }}
        >
            <div className="item">
                {transition ? renderLayer(previousCharacter, "character-slide-current", false) : renderLayer(character, "character-slide-current", true)}
                {transition ? renderLayer(character, "character-slide-incoming", true) : null}
                <div className="button">
                    <button className="btn prev" type="button" onClick={onBack} aria-label="返回角色列表"><img src={mobileImage("backs_03.png")} alt="返回角色列表"/></button>
                    <button className="btn next" type="button" onClick={() => requestCharacterChange(1)} aria-label="下一个角色"><img src={mobileImage("ones_13.png")} alt=""/></button>
                </div>
            </div>
            {isVideoOpen ? (
                <div className="character-video-mask" role="dialog" aria-modal="true" aria-label="角色视频" onClick={() => setIsVideoOpen(false)}>
                    <div className="character-video-dialog" onClick={(event) => event.stopPropagation()}>
                        <video src={videoUrl} controls autoPlay muted playsInline onError={() => setMediaError("角色视频加载失败")}/>
                        <button type="button" className="character-video-close" onClick={() => setIsVideoOpen(false)} aria-label="关闭视频">×</button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
